/**
 * From an account to a company.
 *
 * Three screens, in the order the answers actually matter: who you are, what
 * the company is, and one button that starts the trial. Nothing here can touch
 * tenant data — the whole wizard runs on an onboarding token (see
 * lib/onboarding.ts), and a real session is only minted by the last step, at
 * the moment there is finally a tenant for it to name.
 *
 * THE LAST STEP USED TO BE A PLAN PICKER. It is not any more.
 *
 * A company that has not yet entered a single invoice has no basis on which to
 * compare three tiers — it cannot know how many people will end up in here or
 * how much of the agent it will use. Asking anyway does not produce a decision,
 * it produces a guess, and a guess somebody regrets is a refund rather than a
 * customer. So they get everything for ten days and are told afterwards what
 * their own usage costs: services/planRecommendation.ts works it out, and
 * Settings → Billing shows the arithmetic.
 *
 * The "join instead" branch is not decoration: if somebody at your company
 * already created the workspace and switched on domain joining, offering to
 * create a SECOND company with the same name is the single most confusing thing
 * this page could do.
 */

import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Building2, Check, Loader2, Tag, User, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import {
  clearOnboardingToken,
  getOnboardingToken,
  onboardingApi,
  setOnboardingToken,
  slugify,
} from '@/lib/onboarding';
import { Alert, AlertDescription } from '@/components/shadcn/alert';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import { Skeleton } from '@/components/shadcn/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import { Logo } from '@/components/ui/Logo';
import { TitleField } from '@/components/ui/TitleField';
import { clearPromoRef, readPromoRef, rememberPromoRef } from '@/pages/Signup';
import { cn } from '@/lib/utils';

// ===== Copy =====
// Deliberately local rather than in lib/i18n.ts: the shared dictionary is being
// edited by several people at once and a duplicated key there breaks the build
// for everyone. These strings belong to one flow and nothing else reads them.

const COPY = {
  en: {
    profileTitle: 'Set up your profile',
    profileLede: 'Choose how you will appear in Railit.',
    namePicture: 'Name',
    title: 'Title',
    titlePlaceholder: 'Finance Manager',
    skip: 'Skip',
    continue: 'Continue',
    back: 'Back',
    loggedInAs: 'Logged in as',
    workspaceTitle: 'Create a workspace',
    workspaceLede: 'One company, its own books, its own team.',
    wsName: 'Name',
    wsUrl: 'URL',
    country: 'Country',
    domainJoin: 'Enable access for',
    domainJoinHint: 'Anyone with a verified address on this domain can join without an invite.',
    create: 'Create workspace',
    trialTitle: 'Start with everything',
    trialLede: 'The whole product, switched on. No plan to pick yet.',
    trialEverything: 'Every feature, including the AI agent',
    trialUsers: 'As many users as you want',
    trialCredits: (n: number) => `${n.toLocaleString('en')} AI credits`,
    trialDays: (n: number) => `${n} days`,
    trialNoCard: 'No card. Nothing to cancel.',
    // The promise this page is making about what happens next. It has to be
    // exact, because it is the reason they are not being asked to choose.
    trialAfter:
      'Near the end of the trial we work out which plan your usage actually needs, and show you the arithmetic. You can take it, or take another.',
    startTrial: (n: number) => `Start ${n}-day free trial`,
    joinTitle: 'Join your team',
    joinLede: 'Your colleagues already have a workspace on this domain.',
    join: 'Join',
    createInstead: 'Create a new workspace instead',
    nameRequired: 'Give your workspace a name.',
    extrasTitle: 'Anything else you need?',
    extrasLede: 'Switch on only what you use. Everything here can be turned on later in Settings, and turning one off never deletes anything.',
    m_hr: 'Staff and payroll',
    m_hr_sub: 'Employees, salaries, payslips.',
    m_inventory: 'Inventory',
    m_inventory_sub: 'Products, stock levels and their cost.',
    m_pos: 'Point of sale',
    m_pos_sub: 'Counter sales that post straight to the ledger.',
    m_forms: 'Forms',
    m_forms_sub: 'Collect information from people outside the company.',
    expired: 'This sign-up expired. Start again.',
    promoHave: 'Have a code?',
    promoLabel: 'Discount code',
    promoApply: 'Apply',
    promoChecking: 'Checking…',
    promoRemove: 'Remove',
    promoRefused:
      'That code will not work. Check it with whoever gave it to you, and you can start the trial without it.',
    /* One function rather than five keys, so the joining word and the plural
       are decided in the language that has to say them. */
    promoGives: (days: number, percent: number, months: number) => {
      const extra = days > 0 ? `${days} extra days free` : '';
      const off =
        percent > 0
          ? months > 1
            ? `${percent}% off your first ${months} months`
            : `${percent}% off your first month`
          : '';
      return extra && off ? `${extra}, and ${off}` : extra || off;
    },
  },
  ar: {
    profileTitle: 'أعِدّ ملفك الشخصي',
    profileLede: 'اختر كيف تظهر في وي هاي.',
    namePicture: 'الاسم',
    title: 'المسمّى الوظيفي',
    titlePlaceholder: 'مدير مالي',
    skip: 'تخطّي',
    continue: 'متابعة',
    back: 'رجوع',
    loggedInAs: 'مسجّل الدخول باسم',
    workspaceTitle: 'أنشئ مساحة عمل',
    workspaceLede: 'شركة واحدة، بحساباتها وفريقها.',
    wsName: 'الاسم',
    wsUrl: 'الرابط',
    country: 'الدولة',
    domainJoin: 'السماح بالانضمام لنطاق',
    domainJoinHint: 'أي شخص ببريد مُوثّق على هذا النطاق يمكنه الانضمام دون دعوة.',
    create: 'إنشاء مساحة العمل',
    trialTitle: 'ابدأ بكل شيء',
    trialLede: 'المنتج كاملًا ومفعّل. ولا باقة تختارها الآن.',
    trialEverything: 'كل الميزات، بما فيها وكيل الذكاء الاصطناعي',
    trialUsers: 'عدد المستخدمين كما تشاء',
    trialCredits: (n: number) => `${n.toLocaleString('ar-BH-u-nu-latn')} كريدت ذكاء اصطناعي`,
    trialDays: (n: number) => `${n} أيام`,
    trialNoCard: 'بدون بطاقة. ولا شيء تلغيه.',
    trialAfter:
      'قرب نهاية التجربة نحسب الباقة التي يحتاجها استخدامك فعلًا، ونعرض لك الحساب بالتفصيل. لك أن تأخذها أو تختار غيرها.',
    startTrial: (n: number) => `ابدأ ${n} أيام مجانًا`,
    joinTitle: 'انضم إلى فريقك',
    joinLede: 'زملاؤك لديهم مساحة عمل على هذا النطاق بالفعل.',
    join: 'انضمام',
    createInstead: 'إنشاء مساحة عمل جديدة بدلًا من ذلك',
    nameRequired: 'أعطِ مساحة العمل اسمًا.',
    extrasTitle: 'تحتاج شيئًا آخر؟',
    extrasLede: 'شغّل ما تستخدمه فقط. كل ما هنا يمكن تشغيله لاحقًا من الإعدادات، وإطفاء أي واحد لا يحذف شيئًا.',
    m_hr: 'الموظفون والرواتب',
    m_hr_sub: 'الموظفون والرواتب وقسائم الراتب.',
    m_inventory: 'المخزون',
    m_inventory_sub: 'المنتجات والأرصدة وتكلفتها.',
    m_pos: 'نقاط البيع',
    m_pos_sub: 'مبيعات الكاشير تُرحَّل مباشرة إلى القيود.',
    m_forms: 'النماذج',
    m_forms_sub: 'اجمع معلومات من أشخاص خارج الشركة.',
    expired: 'انتهت صلاحية التسجيل. ابدأ من جديد.',
    promoHave: 'لديك كود؟',
    promoLabel: 'كود الخصم',
    promoApply: 'تطبيق',
    promoChecking: 'جارٍ التحقق…',
    promoRemove: 'إزالة',
    promoRefused: 'هذا الكود لن يعمل. راجعه مع من أعطاك إياه، ويمكنك بدء التجربة بدونه.',
    promoGives: (days: number, percent: number, months: number) => {
      const extra = days > 0 ? `${days} يومًا إضافية مجانًا` : '';
      const off =
        percent > 0
          ? months > 1
            ? `خصم ${percent}٪ على أول ${months} أشهر`
            : `خصم ${percent}٪ على شهرك الأول`
          : '';
      return extra && off ? `${extra}، و${off}` : extra || off;
    },
  },
} as const;

/** Countries we quote a currency for. Anything else defaults to USD. */
const COUNTRIES = [
  { code: 'BH', currency: 'BHD', en: 'Bahrain', ar: 'البحرين' },
  { code: 'SA', currency: 'SAR', en: 'Saudi Arabia', ar: 'السعودية' },
  { code: 'AE', currency: 'AED', en: 'United Arab Emirates', ar: 'الإمارات' },
  { code: 'KW', currency: 'KWD', en: 'Kuwait', ar: 'الكويت' },
  { code: 'QA', currency: 'QAR', en: 'Qatar', ar: 'قطر' },
  { code: 'OM', currency: 'OMR', en: 'Oman', ar: 'عُمان' },
] as const;

// ===== Server shapes =====

interface OnboardingState {
  user: { id: string; email: string; name: string; avatar_url: string | null; title: string | null };
  organizations: { id: string }[];
  suggested: { name: string; slug: string; domain: string | null };
  joinable: { id: string; name: string; name_ar: string | null; slug: string; logo_url: string | null }[];
}

/**
 * Only the trial half of /billing/plans is read here. The tiers, their prices
 * and the first-month offer are all still in that response — this page has
 * simply stopped having an opinion about them.
 */
interface PlansResponse {
  offers: {
    trial_days: number;
    trial_ai_credits: number;
  };
}

interface SessionResponse {
  access: string;
  refresh?: string;
}

/**
 * The optional products offered at sign-up, in the order somebody recognises
 * them. Kept here rather than read from /modules because this screen runs
 * before there is a session to read it with.
 */
const EXTRAS = [
  { code: 'hr', label: 'm_hr', sub: 'm_hr_sub' },
  { code: 'inventory', label: 'm_inventory', sub: 'm_inventory_sub' },
  { code: 'pos', label: 'm_pos', sub: 'm_pos_sub' },
  { code: 'forms', label: 'm_forms', sub: 'm_forms_sub' },
] as const;

type Step = 'profile' | 'workspace' | 'trial';
const STEPS: Step[] = ['profile', 'workspace', 'trial'];

// ===== Chrome =====

function StepDots({ current }: { current: Step }) {
  const index = STEPS.indexOf(current);
  return (
    <div className="flex items-center justify-center gap-1.5" aria-hidden>
      {STEPS.map((s, i) => (
        <span
          key={s}
          className={cn(
            'h-1.5 rounded-full transition-all',
            i === index ? 'w-6 bg-foreground' : 'w-1.5 bg-muted-foreground/30',
          )}
        />
      ))}
    </div>
  );
}

function Frame({
  children,
  email,
  onBack,
  step,
}: {
  children: React.ReactNode;
  email: string | null;
  onBack?: () => void;
  step: Step;
}) {
  const { lang } = useI18n();
  const c = COPY[lang === 'ar' ? 'ar' : 'en'];
  return (
    <div className="flex min-h-svh flex-col">
      <header className="flex items-center justify-between px-6 py-5">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="size-3.5 rtl:rotate-180" />
            {c.back}
          </button>
        ) : (
          <Logo size={22} markOnly />
        )}
        {email ? (
          <div className="text-end text-xs leading-tight">
            <div className="text-muted-foreground">{c.loggedInAs}</div>
            <div className="font-medium">{email}</div>
          </div>
        ) : null}
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">{children}</div>
      </main>

      <footer className="px-6 pb-10">
        <StepDots current={step} />
      </footer>
    </div>
  );
}

/** What `GET /billing/promo/:code` says a code is worth. */
interface PromoOffer {
  code: string;
  trial_bonus_days: number;
  first_month_percent_off: number | null;
  discount_months: number | null;
}

/**
 * "HAVE A CODE?", folded away until somebody says yes.
 *
 * Most people sign up without one, and a discount field open beside the button
 * that starts a free trial is an invitation to leave the page and go looking
 * for a code instead of finishing. It opens by itself for the one visitor who
 * definitely has one: somebody who arrived through a partner link, whose code
 * is already in the box and already checked.
 *
 * WHAT IT SAYS BACK IS THE OFFER, NOT A TICK. "18 extra days free, and 50% off
 * your first month" is the thing they can hold against what the partner told
 * them. A green tick beside a code is only the form agreeing with itself.
 *
 * A REFUSED CODE NEVER BLOCKS THE SIGN-UP. It is reported plainly and the
 * button underneath is untouched. Nothing here can stop a company being
 * created, because a customer lost over a mistyped code is a far worse trade
 * than a discount not given.
 */
function PromoField({
  copy,
  onCode,
}: {
  copy: (typeof COPY)['en'] | (typeof COPY)['ar'];
  onCode: (code: string | null) => void;
}) {
  const [typed, setTyped] = useState(() => readPromoRef() ?? '');
  const [open, setOpen] = useState(() => !!readPromoRef());
  const [checking, setChecking] = useState(false);
  const [offer, setOffer] = useState<PromoOffer | null>(null);
  const [refused, setRefused] = useState(false);

  /*
    Only the LAST lookup may write its answer.

    Four keystrokes are four requests and they do not come back in the order
    they were sent, so without this counter the stale answer for "HHU" lands
    after the good one for "HHUB" and refuses a code that works.
  */
  const attempt = useRef(0);

  /*
    THREE ANSWERS, NOT TWO, AND THE THIRD IS WHY THIS SCREEN STILL WORKS.

    A REFUSAL is `200 { ok: false }`, and only that. The endpoint answers it the
    same way for a code that does not exist, one that lapsed and one that is used
    up, deliberately, so this says one thing back.

    ANYTHING ELSE (a 401, a 500, a dead network) is NOT a refusal, and must not
    be shown as one. Nobody signing up has a session yet, so the lookup can come
    back unauthorised through no fault of the code the visitor holds; telling
    them their partner's code is wrong on the strength of that would be a lie
    they cannot argue with. So the code is passed on unchecked instead. It costs
    nothing to be wrong that way: `/onboarding/workspace` resolves it quietly and
    the whole attribution is best effort behind a `.catch`, so a bad code cannot
    take the sign-up down with it.
  */
  const check = async (raw: string) => {
    const code = raw.trim().toUpperCase();
    const mine = ++attempt.current;
    setOffer(null);
    setRefused(false);
    onCode(null);
    if (code.length < 2) {
      setChecking(false);
      return;
    }
    setChecking(true);
    try {
      const res = await fetch(`/api/erp/billing/promo/${encodeURIComponent(code)}`);
      const body = (await res.json().catch(() => ({}))) as { ok?: boolean; offer?: PromoOffer };
      if (mine !== attempt.current) return;
      if (res.ok && body.ok && body.offer) {
        setOffer(body.offer);
        onCode(body.offer.code);
      } else if (res.ok && body.ok === false) {
        setRefused(true);
      } else {
        onCode(code);
      }
    } catch {
      if (mine === attempt.current) onCode(code);
    } finally {
      if (mine === attempt.current) setChecking(false);
    }
  };

  /* The prefilled code is checked once, unasked, so the visitor sees what the
     link promised them without having to press anything. */
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current) return;
    prefilled.current = true;
    const carried = readPromoRef();
    if (carried) void check(carried);
    // `check` is stable enough for one call on mount; re-running this on every
    // render would issue a lookup per keystroke against a public endpoint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-4 flex items-center gap-1.5 self-start text-[12.5px] text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
      >
        <Tag className="size-3.5" />
        {copy.promoHave}
      </button>
    );
  }

  return (
    <div className="mt-5 grid gap-1.5">
      <Label htmlFor="ob-promo" className="text-[12.5px]">
        {copy.promoLabel}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id="ob-promo"
          value={typed}
          dir="ltr"
          autoCapitalize="characters"
          spellCheck={false}
          className="h-9 font-mono text-sm uppercase"
          onChange={(e) => setTyped(e.target.value)}
          onBlur={() => void check(typed)}
          onKeyDown={(e) => {
            /* Enter checks the code. It must not submit: the button below is
               the one that creates a company. */
            if (e.key === 'Enter') {
              e.preventDefault();
              void check(typed);
            }
          }}
        />
        {offer ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={copy.promoRemove}
            onClick={() => {
              attempt.current += 1;
              setTyped('');
              setOffer(null);
              setRefused(false);
              onCode(null);
              /* Forgotten for good: a code the customer took out of the box
                 must not come back on the next screen that reads the stash. */
              clearPromoRef();
            }}
          >
            <X className="size-4" />
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 shrink-0"
            disabled={checking || typed.trim().length < 2}
            onClick={() => void check(typed)}
          >
            {checking ? <Loader2 className="size-3.5 animate-spin" /> : null}
            {checking ? copy.promoChecking : copy.promoApply}
          </Button>
        )}
      </div>
      {offer ? (
        <p className="flex items-start gap-1.5 text-[12.5px] leading-relaxed text-brand-ink">
          <Check className="mt-0.5 size-3.5 shrink-0" />
          <span>
            {copy.promoGives(
              offer.trial_bonus_days,
              offer.first_month_percent_off ?? 0,
              offer.discount_months ?? 1,
            )}
          </span>
        </p>
      ) : refused ? (
        <p role="status" className="text-[12.5px] leading-relaxed text-muted-foreground">
          {copy.promoRefused}
        </p>
      ) : null}
    </div>
  );
}

// ===== The page =====

export function Onboarding() {
  const { lang } = useI18n();
  const { isAuthenticated, adoptSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const c = COPY[lang === 'ar' ? 'ar' : 'en'];
  const ar = lang === 'ar';

  /**
   * Coming back from Google, the token arrives in the URL FRAGMENT — never the
   * query string, which would land in server logs and Referer headers. Store it
   * and scrub the address bar before anything else renders.
   */
  const [tokenReady, setTokenReady] = useState(() => !!getOnboardingToken());
  useEffect(() => {
    /* A partner link opened straight at this screen. Read before the address
       bar is scrubbed two lines down, which takes the query string with it. */
    rememberPromoRef(params.get('ref'));
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const fromGoogle = hash.get('onboarding') ?? params.get('onboarding');
    if (fromGoogle) {
      setOnboardingToken(fromGoogle);
      window.history.replaceState({}, '', window.location.pathname);
      setTokenReady(true);
    }
  }, [params]);

  const [step, setStep] = useState<Step>('profile');
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [wsName, setWsName] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slug, setSlug] = useState('');
  const [country, setCountry] = useState('BH');
  /*
    THE MODULES A NEW WORKSPACE DOES NOT GET UNTIL IT ASKS.

    erp, finance, crm and the assistant are on from the start: they are the
    ledger, the documents, the customers and the thing people signed up for.
    These four are not, because each is a real product that most customers do
    not want, and the day Inventory shipped a software company with no shelves
    acquired a Stock screen, a Movements ledger and three stock tools in its
    assistant. Handing somebody eight things and letting them work out which
    two they needed makes the first hour harder, not richer.

    It also costs the customer money. Every enabled module puts its tools in
    front of the model on every turn, and the benchmark is blunt about what
    that does: four tools added in one morning measurably worsened tool
    selection across every model tested.
  */
  const [extras, setExtras] = useState<string[]>([]);
  /**
   * A discount code, once the server has said it is a real one.
   *
   * Null covers "none typed", "still checking" and "refused" alike, because
   * creating the company treats all three identically: it goes ahead. The
   * server has its own copy anyway (the signed cookie the partner link set, and
   * `User.signup_promo_code` written at register), so this is the visitor's
   * explicit answer and not the only chance the code has to land.
   */
  const [promoCode, setPromoCode] = useState<string | null>(null);
  /* Domain joining is not offered at sign-up — see the note in the workspace
     step. Sent as false rather than omitted so the server's default and the
     client's intent are the same thing written down. */
  const domainJoin = false;
  const [showJoin, setShowJoin] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const state = useQuery<OnboardingState>({
    queryKey: ['onboarding', 'state'],
    queryFn: () => onboardingApi<OnboardingState>('/auth/onboarding'),
    enabled: tokenReady,
    retry: false,
  });

  const plans = useQuery<PlansResponse>({
    queryKey: ['onboarding', 'plans'],
    queryFn: async () => (await fetch('/api/erp/billing/plans')).json(),
    staleTime: 5 * 60_000,
  });

  // Prefill once the server has told us who this is.
  useEffect(() => {
    if (!state.data) return;
    setName((prev) => prev || state.data.user.name);
    setTitle((prev) => prev || (state.data.user.title ?? ''));
    setWsName((prev) => prev || state.data.suggested.name);
    setSlug((prev) => prev || state.data.suggested.slug);
  }, [state.data]);

  // The slug follows the name until somebody edits it, and then it is theirs.
  useEffect(() => {
    if (!slugTouched) setSlug(slugify(wsName));
  }, [wsName, slugTouched]);

  const currency = COUNTRIES.find((x) => x.code === country)?.currency ?? 'USD';
  const domain = state.data?.suggested.domain ?? null;
  const joinable = state.data?.joinable ?? [];

  const saveProfile = useMutation({
    mutationFn: () =>
      onboardingApi<{ onboarding: string }>('/auth/onboarding/profile', {
        method: 'PATCH',
        body: JSON.stringify({ name: name.trim(), title: title.trim() || undefined, locale: ar ? 'ar' : 'en' }),
      }),
    onSuccess: (res) => {
      // The token is re-issued on every step so an abandoned wizard resumed an
      // hour later is refused rather than half-working.
      setOnboardingToken(res.onboarding);
      setStep('workspace');
    },
    onError: (e: Error) => setError(e.message),
  });

  /**
   * `to` because creating a company and joining one arrive differently.
   *
   * A brand new workspace is an empty accounting system, so it lands on /start:
   * three doors, one question, where is your company's history right now.
   * Somebody joining a colleague's workspace lands on the dashboard, because
   * the books are already in here and offering to import them again would be
   * alarming rather than helpful.
   */
  const finish = async (res: SessionResponse, to?: string) => {
    clearOnboardingToken();
    /* The company exists, so the code has done its work. Left behind, it would
       be offered again to the next workspace created in this tab, and a code
       with a cap would be spent twice by one person. */
    clearPromoRef();
    await adoptSession(res, to);
  };

  const createWorkspace = useMutation({
    /*
      No `plan_code`. The trial is capability-open whatever tier is seeded
      (lib/pricing.ts TRIAL_CAPABILITIES), so the seed is a label, not a
      decision — and the server owns which one it is. Sending a code from here
      would put a plan choice back into sign-up through the API instead of the
      UI, and would hardcode a tier outside pricing.ts to do it.
    */
    mutationFn: () =>
      onboardingApi<SessionResponse>('/auth/onboarding/workspace', {
        method: 'POST',
        body: JSON.stringify({
          name: wsName.trim(),
          slug: slug || undefined,
          country,
          base_currency: currency,
          /* Only the extras: the server always re-adds the core, so a short
             list can never produce a workspace with no ledger. */
          modules: extras,
          locale: ar ? 'ar' : 'en',
          allow_domain_join: !!domain && domainJoin,
          /* Omitted rather than sent as null: the schema on the other side
             takes an optional string of at least two characters, and a null
             would fail the whole request over a discount nobody typed. */
          ...(promoCode ? { promo_code: promoCode } : {}),
        }),
      }),
    onSuccess: (res) => finish(res, '/start'),
    onError: (e: Error) => setError(e.message),
  });

  const joinWorkspace = useMutation({
    mutationFn: (orgId: string) =>
      onboardingApi<SessionResponse>('/auth/onboarding/join', {
        method: 'POST',
        body: JSON.stringify({ org_id: orgId }),
      }),
    onSuccess: finish,
    onError: (e: Error) => setError(e.message),
  });

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  if (!tokenReady && !getOnboardingToken()) return <Navigate to="/signup" replace />;

  if (state.isError) {
    return (
      <Frame email={null} step="profile">
        <Alert variant="destructive">
          <AlertDescription>{c.expired}</AlertDescription>
        </Alert>
        <Button className="mt-6 w-full" onClick={() => navigate('/signup', { replace: true })}>
          {c.back}
        </Button>
      </Frame>
    );
  }

  // Already has a company — the wizard has nothing left to ask.
  if (state.data && state.data.organizations.length > 0) {
    return <Navigate to="/login" replace />;
  }

  const email = state.data?.user.email ?? null;

  if (state.isLoading) {
    return (
      <Frame email={null} step="profile">
        <div className="space-y-4">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </Frame>
    );
  }

  const errorBanner = error ? (
    <Alert variant="destructive" role="alert" className="mb-6">
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  ) : null;

  // ---- Join your team ----
  if (step === 'workspace' && showJoin && joinable.length > 0) {
    return (
      <Frame email={email} step="workspace" onBack={() => setStep('profile')}>
        {errorBanner}
        <h1 className="text-2xl font-semibold tracking-tight">{c.joinTitle}</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">{c.joinLede}</p>

        <ul className="mt-8 grid gap-2">
          {joinable.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                onClick={() => joinWorkspace.mutate(o.id)}
                disabled={joinWorkspace.isPending}
                className="flex w-full items-center gap-3 rounded-lg border px-4 py-3.5 text-start transition-colors hover:bg-accent disabled:opacity-60"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted">
                  <Building2 className="size-4 text-muted-foreground" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {ar ? (o.name_ar ?? o.name) : o.name}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    railit.ai/{o.slug}
                  </span>
                </span>
                <span className="shrink-0 text-sm font-medium text-muted-foreground">{c.join}</span>
              </button>
            </li>
          ))}
        </ul>

        <Button variant="ghost" className="mt-6 w-full" onClick={() => setShowJoin(false)}>
          {c.createInstead}
        </Button>
      </Frame>
    );
  }

  // ---- Step 1: profile ----
  if (step === 'profile') {
    return (
      <Frame email={email} step="profile">
        {errorBanner}
        <h1 className="text-2xl font-semibold tracking-tight">{c.profileTitle}</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">{c.profileLede}</p>

        <form
          className="mt-8 space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim().length >= 2) saveProfile.mutate();
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="ob-name">{c.namePicture}</Label>
            <div className="flex items-center gap-3">
              <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
                {state.data?.user.avatar_url ? (
                  <img
                    src={state.data.user.avatar_url}
                    alt=""
                    className="size-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <User className="size-4 text-muted-foreground" />
                )}
              </span>
              <Input
                id="ob-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ahmed Al-Dakheel"
                autoFocus
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ob-title">{c.title}</Label>
            <TitleField id="ob-title" value={title} onChange={setTitle} />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setStep('workspace')}>
              {c.skip}
            </Button>
            <Button type="submit" disabled={name.trim().length < 2 || saveProfile.isPending}>
              {saveProfile.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              {c.continue}
            </Button>
          </div>
        </form>
      </Frame>
    );
  }

  // ---- Step 2: the company ----
  if (step === 'workspace') {
    return (
      <Frame email={email} step="workspace" onBack={() => setStep('profile')}>
        {errorBanner}
        <h1 className="text-2xl font-semibold tracking-tight">{c.workspaceTitle}</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">{c.workspaceLede}</p>

        <form
          className="mt-8 space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (wsName.trim().length >= 2) setStep('trial');
            else setError(c.nameRequired);
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="ob-ws">{c.wsName}</Label>
            <Input
              id="ob-ws"
              value={wsName}
              onChange={(e) => setWsName(e.target.value)}
              placeholder="Acme Trading W.L.L."
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ob-slug">{c.wsUrl}</Label>
            <div className="flex items-stretch overflow-hidden rounded-md border focus-within:ring-[3px] focus-within:ring-ring/50">
              <span className="flex items-center bg-muted px-3 text-sm text-muted-foreground">
                railit.ai/
              </span>
              <input
                id="ob-slug"
                dir="ltr"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(slugify(e.target.value));
                }}
                className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm outline-none"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ob-country">{c.country}</Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger id="ob-country" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((x) => (
                  <SelectItem key={x.code} value={x.code}>
                    {ar ? x.ar : x.en} · {x.currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/*
            FOUR CHECKBOXES, NOT EIGHT, AND NONE OF THEM TICKED.

            Only the modules where a person genuinely knows the answer before
            they have seen the product: a salon knows it has no warehouse. The
            assistant is not on this list, because asking somebody to opt into
            the thing they signed up for, ninety seconds in and before they have
            seen it work, is asking them to decline it.

            The line about Settings is load bearing. Somebody who is not sure
            will leave a box unticked, and they need to know that is not a door
            closing.
          */}
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium">{c.extrasTitle}</p>
              <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{c.extrasLede}</p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {EXTRAS.map((m) => {
                const on = extras.includes(m.code);
                return (
                  <button
                    key={m.code}
                    type="button"
                    onClick={() =>
                      setExtras((prev) =>
                        on ? prev.filter((x) => x !== m.code) : [...prev, m.code],
                      )
                    }
                    aria-pressed={on}
                    className={cn(
                      'rounded-lg border p-3 text-start transition-colors',
                      on ? 'border-foreground/40 bg-accent' : 'border-border hover:bg-accent/40',
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className={cn(
                          'flex size-4 shrink-0 items-center justify-center rounded-[4px] border',
                          on ? 'border-foreground bg-foreground text-background' : 'border-border',
                        )}
                        aria-hidden
                      >
                        {on ? <Check className="size-3" /> : null}
                      </span>
                      <span className="text-[13px] font-medium">{c[m.label]}</span>
                    </span>
                    <span className="mt-1 block ps-6 text-[12px] leading-relaxed text-muted-foreground">
                      {c[m.sub]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/*
            Domain joining is deliberately not offered.

            The server still supports it (auth.ts `allow_domain_join`), and the
            "join your team" branch above still works for a workspace that has
            it on — but nobody is asked to switch it on at sign-up. Letting
            anyone with a matching email address walk into a company's ledger is
            a decision that needs more context than a founder has in the first
            two minutes, and defaulting it ON, as this did, is the wrong way
            round for a system holding somebody's books.
          */}

          <Button type="submit" size="lg" className="h-11 w-full">
            {c.continue}
            <ArrowRight className="size-4 rtl:rotate-180" />
          </Button>
        </form>
      </Frame>
    );
  }

  // ---- Step 3: the trial ----
  const trial = plans.data?.offers;
  /*
    Two numbers, treated differently on purpose.

    The day count carries a fallback because it is inside the button's own
    label and the button has to render even if /billing/plans has not answered
    — and ten days is the same claim the marketing site made to get them here.
    The credit allowance does not: a wrong allowance is a promise we cannot
    keep, so if we have not read it, that line is simply absent.
  */
  const days = trial?.trial_days ?? 10;
  const facts = [
    c.trialEverything,
    c.trialUsers,
    trial ? c.trialCredits(trial.trial_ai_credits) : null,
    c.trialDays(days),
    c.trialNoCard,
  ].filter((f): f is string => !!f);

  return (
    <Frame email={email} step="trial" onBack={() => setStep('workspace')}>
      {errorBanner}
      <h1 className="text-2xl font-semibold tracking-tight">{c.trialTitle}</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">{c.trialLede}</p>

      <ul className="mt-8 grid gap-2.5">
        {facts.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm">
            <Check className="mt-0.5 size-4 shrink-0 text-brand-ink" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {/*
        THE CODE GOES HERE, not on the company screen.

        This is the list of what the trial gives, and a code changes that list:
        it adds free days to the very number printed in the button below it. On
        the previous screen the same field would have been a discount question
        stranded among a name, a URL and a country.
      */}
      <div className="flex flex-col">
        <PromoField copy={c} onCode={setPromoCode} />
      </div>

      <Button
        size="lg"
        className="mt-8 h-11 w-full"
        disabled={createWorkspace.isPending}
        onClick={() => createWorkspace.mutate()}
      >
        {createWorkspace.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
        {c.startTrial(days)}
        <ArrowRight className="size-4 rtl:rotate-180" />
      </Button>

      <p className="mt-4 text-center text-xs leading-relaxed text-muted-foreground">
        {c.trialAfter}
      </p>
    </Frame>
  );
}
